import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Input,
  Pagination,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { Download, Search, SlidersHorizontal } from "lucide-react";
import useMetadataStore from "stores/metadataStore";
import { useLocaleStore } from "stores/localeStore";
import { downloadSupplierProductsZip } from "../hooks/downloadSupplierProductsZip";
import {
  DEFAULT_SUPPLIER_PRODUCTS_PAGE_SIZE,
  useSupplierProductsQuery,
} from "../hooks/useSupplierProductsQuery";
import type { SupplierProductListItem, SupplierQuickFilter } from "../types";
import { SupplierProductListCard } from "./SupplierProductListCard";
import { SupplierProductSheet } from "./SupplierProductSheet";

const { Title, Text } = Typography;

const QUICK_FILTERS: { key: SupplierQuickFilter; label: string }[] = [
  { key: "all", label: "Alla" },
  { key: "published", label: "Publicerade" },
  { key: "has-material", label: "Material finns" },
  { key: "missing-material", label: "Material saknas" },
  { key: "updated-30d", label: "Uppdaterade senaste 30 dagarna" },
];

/** Rough estimate until a ZIP/download API exposes real file sizes. */
const ESTIMATED_BYTES_PER_FILE = 350_000;
const DOWNLOAD_LIMIT_BYTES = 100 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toLocaleString("sv-SE", {
      maximumFractionDigits: 1,
    })} KB`;
  }
  return `${(bytes / (1024 * 1024)).toLocaleString("sv-SE", {
    maximumFractionDigits: 1,
  })} MB`;
}

function latestMaterialDate(items: SupplierProductListItem[]): string | null {
  const timestamps = items
    .map((p) => (p.modifiedAt ? Date.parse(p.modifiedAt) : NaN))
    .filter((n) => !Number.isNaN(n));
  if (timestamps.length === 0) {
    return null;
  }
  return new Date(Math.max(...timestamps)).toISOString().slice(0, 10);
}

export const SupplierProductListView: React.FC = () => {
  const { locale } = useLocaleStore();
  const { metadata } = useMetadataStore();

  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<SupplierQuickFilter>("all");
  const [groupId, setGroupId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Map<number, SupplierProductListItem>>(
    new Map(),
  );
  const [downloading, setDownloading] = useState(false);
  const [sheetProductId, setSheetProductId] = useState<number | null>(null);

  const { data, isLoading, isFetching } = useSupplierProductsQuery({
    q,
    filter,
    groupId,
    page,
    pageSize: DEFAULT_SUPPLIER_PRODUCTS_PAGE_SIZE,
    locale,
  });

  const products = data?.data ?? [];
  const total = data?.total ?? 0;
  const loading = isLoading || isFetching;

  const groupOptions = useMemo(
    () =>
      (metadata?.productGroups ?? []).map((group) => ({
        value: group.id,
        label: group.number
          ? `${group.name} (${group.number})`
          : group.name ?? String(group.id),
      })),
    [metadata?.productGroups],
  );

  const summary = useMemo(() => {
    let files = 0;
    selected.forEach((product) => {
      files += product.imageCount + product.docCount;
    });
    return {
      count: selected.size,
      files,
      bytes: files * ESTIMATED_BYTES_PER_FILE,
    };
  }, [selected]);

  const overLimit = summary.bytes > DOWNLOAD_LIMIT_BYTES;
  const materialUpdated = latestMaterialDate(products);

  const search = () => {
    setPage(1);
    setQ(qInput.trim());
  };

  const toggle = (product: SupplierProductListItem) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(product.id)) {
        next.delete(product.id);
      } else {
        next.set(product.id, product);
      }
      return next;
    });
  };

  const pageSelectedCount = products.filter((p) => selected.has(p.id)).length;
  const allOnPageSelected =
    products.length > 0 && pageSelectedCount === products.length;
  const someOnPageSelected =
    pageSelectedCount > 0 && pageSelectedCount < products.length;

  const selectPage = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      products.forEach((product) => next.set(product.id, product));
      return next;
    });
  };

  const deselectPage = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      products.forEach((product) => next.delete(product.id));
      return next;
    });
  };

  const toggleSelectPage = () => {
    if (allOnPageSelected) {
      deselectPage();
    } else {
      selectPage();
    }
  };

  const onOpen = (product: SupplierProductListItem) => {
    setSheetProductId(product.id);
  };

  const onDownload = async () => {
    if (overLimit || downloading || selected.size === 0) {
      return;
    }
    setDownloading(true);
    try {
      await downloadSupplierProductsZip({
        productIds: [...selected.keys()],
      });
      message.success("ZIP-filen laddas ner");
    } catch (error) {
      const text =
        error instanceof Error ? error.message : "Kunde inte ladda ner ZIP";
      message.error(text);
    } finally {
      setDownloading(false);
    }
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * DEFAULT_SUPPLIER_PRODUCTS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * DEFAULT_SUPPLIER_PRODUCTS_PAGE_SIZE, total);

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ margin: 0 }}>
          Produkter
        </Title>
        <Text type="secondary">
          {total.toLocaleString("sv-SE")} artiklar hos Verktygsboden
          {materialUpdated ? ` · material uppdaterat ${materialUpdated}` : ""}
        </Text>
      </div>

      <Card bordered={false} bodyStyle={{ padding: 16 }}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              size="large"
              allowClear
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onPressEnter={search}
              prefix={
                <Search size={17} strokeWidth={1.75} color="rgba(0,0,0,0.35)" />
              }
              placeholder={`Sök bland dina ${total.toLocaleString("sv-SE")} artiklar – artikelnummer, EAN eller namn`}
              style={{ flex: 1, minWidth: 0 }}
            />
            <Select
              size="large"
              allowClear
              value={groupId ?? undefined}
              onChange={(value) => {
                setGroupId(value ?? null);
                setPage(1);
              }}
              placeholder="Varugrupp: alla"
              style={{ width: 320 }}
              popupMatchSelectWidth={360}
              suffixIcon={<SlidersHorizontal size={15} strokeWidth={1.75} />}
              options={groupOptions}
              showSearch
              optionFilterProp="label"
            />
            <Button size="large" type="primary" onClick={search}>
              Sök
            </Button>
          </Space.Compact>

          <Space size={8} wrap>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Snabbfilter
            </Text>
            {QUICK_FILTERS.map((f) => (
              <Tag.CheckableTag
                key={f.key}
                checked={filter === f.key}
                onChange={() => {
                  setFilter(f.key);
                  setPage(1);
                }}
              >
                {f.label}
              </Tag.CheckableTag>
            ))}
          </Space>
        </Space>
      </Card>

      {summary.count > 0 && (
        <Alert
          type={overLimit ? "warning" : "info"}
          showIcon
          message={
            <Space wrap size={14} style={{ width: "100%" }}>
              <Text strong>{summary.count} produkter valda</Text>
              <Text>
                · {summary.files} filer · {formatBytes(summary.bytes)}
              </Text>
              <a onClick={selectPage}>
                Välj alla {products.length} på denna sida
              </a>
              <a onClick={() => setSelected(new Map())}>Rensa val</a>
              {overLimit && (
                <Text type="warning">gränsen är 100 MB per nedladdning</Text>
              )}
            </Space>
          }
          action={
            <Space>
              <Button
                onClick={() =>
                  message.info("Filtypsval kommer snart")
                }
              >
                Välj filtyper…
              </Button>
              <Button
                type="primary"
                disabled={overLimit || downloading}
                loading={downloading}
                icon={<Download size={15} strokeWidth={1.75} />}
                onClick={onDownload}
              >
                Ladda ner val (ZIP)
              </Button>
            </Space>
          }
        />
      )}

      {!loading && products.length === 0 ? (
        <Empty description="Inga artiklar matchar sökningen" />
      ) : (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {products.length > 0 && (
            <Checkbox
              checked={allOnPageSelected}
              indeterminate={someOnPageSelected}
              onChange={toggleSelectPage}
            >
              Välj alla på denna sida ({products.length})
            </Checkbox>
          )}
          <Row gutter={[16, 16]}>
            {(loading && products.length === 0
              ? Array.from({ length: 8 }, (_, i) => ({
                  id: -i - 1,
                  name: "",
                  sku: "",
                  imageCount: 0,
                  docCount: 0,
                  published: false,
                }))
              : products
            ).map((product) => {
              const checked = selected.has(product.id);
              return (
                <Col
                  key={product.id}
                  xs={24}
                  sm={12}
                  md={8}
                  lg={6}
                  xl={4}
                  xxl={4}
                  style={{ display: "flex" }}
                >
                  <SupplierProductListCard
                    product={product}
                    selected={checked}
                    loading={loading && products.length === 0}
                    onToggle={toggle}
                    onOpen={onOpen}
                  />
                </Col>
              );
            })}
          </Row>
        </Space>
      )}

      <Row justify="space-between" align="middle">
        <Text type="secondary">
          Visar {rangeStart}–{rangeEnd} av {total.toLocaleString("sv-SE")}
        </Text>
        <Pagination
          current={page}
          pageSize={DEFAULT_SUPPLIER_PRODUCTS_PAGE_SIZE}
          total={total}
          showSizeChanger={false}
          onChange={(pg) => setPage(pg)}
        />
      </Row>

      <SupplierProductSheet
        open={sheetProductId != null}
        productId={sheetProductId}
        onClose={() => setSheetProductId(null)}
      />
    </Space>
  );
};
